/*
 * Paper or real, for the whole account.
 *
 * WHY THIS IS ONE SWITCH AND NOT A SETTING PER STRATEGY
 *
 * It used to be per strategy, and that is a way to be wrong quietly: four
 * rules, three of them moved across, one forgotten, and no screen that says so
 * at a glance. An environment is a thing a person can hold in their head —
 * "I am on paper today" — and every page can then be trusted to mean it.
 *
 * WHY GOING TO REAL ASKS FOR A CODE
 *
 * Because this single click turns every simulated strategy into one that
 * spends money, and a click is exactly what somebody who has borrowed an
 * unlocked laptop would have. The code proves the phone is present. Coming
 * back to paper asks for nothing: friction on the way to safety only teaches
 * people to click through prompts.
 *
 * The switch is in the top bar, beside the market state and the halt button,
 * because those three are what must never be scrolled away from.
 */

import { useEffect, useRef, useState } from "react";

import { useRequestEnvironmentOtp, useSetEnvironment, useStatus } from "../api/hooks";
import { IconCheck, IconWarning } from "./Icons";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

export function EnvironmentSwitch() {
  const status = useStatus();
  const setEnvironment = useSetEnvironment();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const environment = status.data?.environment ?? "paper";
  const connected = status.data?.connected ?? false;
  const live = environment === "live";

  const toPaper = () =>
    setEnvironment.mutate(
      { environment: "paper" },
      {
        onSuccess: (r) =>
          toast.ok(
            `Back on paper. ${r.strategiesChanged} strateg${
              r.strategiesChanged === 1 ? "y is" : "ies are"
            } simulated again.`,
          ),
        onError: (e) => toast.fail(e),
      },
    );

  return (
    <>
      <div
        className={`env-switch ${live ? "live" : "paper"}`}
        role="group"
        aria-label="Trading environment"
      >
        <button
          className={!live ? "on" : ""}
          onClick={() => !live || toPaper()}
          disabled={setEnvironment.isPending}
          aria-pressed={!live}
          title="Simulated money. Nothing reaches Zerodha."
        >
          Paper
        </button>
        <button
          className={live ? "on" : ""}
          onClick={() => live || setConfirming(true)}
          disabled={setEnvironment.isPending || !connected}
          aria-pressed={live}
          title={
            connected
              ? "Real money, at Zerodha."
              : "Connect a Zerodha account first."
          }
        >
          Real
        </button>
      </div>

      {confirming && <GoLiveDialog onClose={() => setConfirming(false)} />}
    </>
  );
}

/**
 * The code, and the plain statement of what is about to change.
 *
 * It names the number of strategies rather than saying "your strategies",
 * because four is a different decision from one and the person should be
 * reading a fact, not a category.
 */
function GoLiveDialog({ onClose }: { onClose: () => void }) {
  const status = useStatus();
  const requestOtp = useRequestEnvironmentOtp();
  const setEnvironment = useSetEnvironment();
  const toast = useToast();

  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [tail, setTail] = useState("");
  const [countdown, setCountdown] = useState(0);
  const field = useRef<HTMLInputElement>(null);

  const strategies = status.data?.strategies ?? 0;
  const openPaper = status.data?.paperDeployed ?? 0;

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const send = () =>
    requestOtp.mutate(undefined, {
      onSuccess: (r) => {
        setSent(true);
        setTail(r.mobile);
        setCountdown(60);
        window.setTimeout(() => field.current?.focus(), 50);
      },
      onError: (e) => toast.fail(e),
    });

  const go = () =>
    setEnvironment.mutate(
      { environment: "live", otp },
      {
        onSuccess: (r) => {
          toast.ok(`${r.strategiesChanged} strategies are now real. Arm the account to start.`);
          onClose();
        },
        onError: (e) => {
          toast.fail(e);
          setOtp("");
          field.current?.focus();
        },
      },
    );

  return (
    <Modal
      title="Switch to real trading"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          {!sent ? (
            <button className="btn danger" onClick={send} disabled={requestOtp.isPending}>
              {requestOtp.isPending ? <span className="spinner" /> : "Text me a code"}
            </button>
          ) : (
            <button
              className="btn danger"
              onClick={go}
              disabled={setEnvironment.isPending || otp.length < 4}
            >
              {setEnvironment.isPending ? <span className="spinner" /> : "Switch to real"}
            </button>
          )}
        </>
      }
    >
      <div className="banner danger" style={{ marginTop: 0 }}>
        <span className="ico">
          <IconWarning size={17} />
        </span>
        <div className="body">
          <strong>
            {strategies} strateg{strategies === 1 ? "y" : "ies"} will start spending real money
          </strong>
          <p>
            Every one of them moves from simulated to real at once. The per-trade sizes,
            trade counts and loss limits you set for live trading all come back into
            force — on paper they were ignored.
          </p>
        </div>
      </div>

      <div className="col" style={{ gap: 0 }}>
        <Row k="Real capital" v={rupeesOf(status.data?.capitalRupees)} />
        <Row k="Most trades a day" v={String(status.data?.accountMaxTradesPerDay ?? "—")} />
        <Row k="Halts after losing" v={rupeesOf(status.data?.accountDailyLossRupees)} />
      </div>

      <p className="small muted mt">
        Open paper positions are left alone and still close on paper — a position
        always exits the way it entered. The account will be <strong>halted</strong>{" "}
        afterwards, so nothing trades until you arm it.
        {openPaper > 0 && ` You currently hold ₹${Math.round(openPaper).toLocaleString("en-IN")} of simulated positions.`}
      </p>

      {sent && (
        <div className="field mt">
          <label htmlFor="envOtp">Code texted to the number ending {tail}</label>
          <input
            id="envOtp"
            ref={field}
            className="otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          />
          <button
            className="btn ghost sm mt"
            style={{ alignSelf: "flex-start" }}
            disabled={countdown > 0 || requestOtp.isPending}
            onClick={send}
          >
            {countdown > 0 ? `Send again in ${countdown}s` : "Send another code"}
          </button>
        </div>
      )}
    </Modal>
  );
}

function rupeesOf(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return "₹" + value.toLocaleString("en-IN");
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="switch-row">
      <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>
        {k}
      </div>
      <div className="num" style={{ fontWeight: 600, fontSize: 13 }}>
        {v}
      </div>
    </div>
  );
}

/** A small always-on reminder, for pages that are not the dashboard. */
export function EnvironmentBadge() {
  const status = useStatus();
  const live = status.data?.environment === "live";
  return (
    <span className={`pill ${live ? "danger" : "paper"}`}>
      {live ? (
        <>
          <IconCheck size={11} /> Real money
        </>
      ) : (
        "Paper"
      )}
    </span>
  );
}
